import { Endpoints, fetch, normalizeKeys, post, remove } from '../api'

// The two structure calls reject rather than resolving to an empty body: the report page is the
// one screen that can tell the reader why it is empty, and a denial has to reach it to be shown.
export const fetchReportById = async ({ reportId }) => {
  const uri = `${Endpoints.Report}/${reportId}/Structure`
  const response = await fetch(uri, { throwOnError: true })
  const normalizedReport = normalizeKeys(response.data)
  return normalizedReport
}

const liveReportBase = ({ productId, planId, snapshotId }) =>
  snapshotId
    ? `${Endpoints.LiveReport}/${productId}/${planId}/snapshot/${snapshotId}`
    : `${Endpoints.LiveReport}/${productId}/${planId}`

export const fetchLiveReportStructureById = async ({ productId, planId, snapshotId }) => {
  const uri = `${liveReportBase({ productId, planId, snapshotId })}/Structure`
  const response = await fetch(uri, { throwOnError: true })
  const structure = normalizeKeys(response.data)

  const sections = structure?.components?.reduce((acc, component) => {
    acc[component.id] = component.name
    return acc
  }, {})

  return {
    id: 'LiveReport',
    name: 'Report:',
    planId: planId,
    productId: productId.substring(0, 2),
    sections: sections,
    components: structure.components,
  }
}

export const deleteReportById = async reportId => {
  const uri = `${Endpoints.Report}/${reportId}`
  return remove(uri)
}

export const fetchReportInfosByProductId = async ({ planId, productId }) => {
  const uri = `${Endpoints.Report}/?PlanId=${planId}&productId=${productId}`
  // Entitlement-gated, so a 403 has to reach the caller: swallowed it would look like a plan with
  // no reports.
  const response = await fetch(uri, { throwOnError: true })
  return normalizeKeys(response.data)
}

export const fetchAllReportsByProductId = async ({ planId, productId }) => {
  const [regularReports, snapshots] = await Promise.all([
    fetchReportInfosByProductId({ planId, productId }),
    productId === 'pa'
      ? fetchLiveReportSnapshots({ productId, planId })
      : Promise.resolve([]),
  ])

  const snapshotCards = (Array.isArray(snapshots) ? snapshots : []).map(snapshot => ({
    id: `snapshot-${snapshot.snapshotId}`,
    snapshotId: snapshot.snapshotId,
    name: snapshot.name,
    isSnapshot: true,
    description: formatSnapshotSubtitle(snapshot.createdAtUtc),
    planId,
    productId: productId.substring(0, 2),
  }))

  return [
    ...snapshotCards,
    ...(Array.isArray(regularReports) ? regularReports : []),
  ]
}

const formatSnapshotSubtitle = createdAtUtc => {
  if (!createdAtUtc) return 'Snapshot'
  const date = new Date(createdAtUtc)
  if (isNaN(date.getTime())) return 'Snapshot'
  return `Snapshot · ${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

export const createLiveReportSnapshot = async ({ productId, planId, name, asOf, dataYear }) => {
  const uri = `${Endpoints.LiveReport}/${productId}/${planId}/snapshot`
  const response = await post(
    uri,
    { name, asOf, dataYear },
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      responseType: 'json',
    },
  )
  const snapshotId = response.data
  if (typeof snapshotId !== 'string' || !snapshotId.trim()) {
    throw new Error('Snapshot creation did not return a valid snapshot id')
  }
  return snapshotId
}

export const fetchLiveReportSnapshots = async ({ productId, planId }) => {
  const uri = `${Endpoints.LiveReport}/${productId}/${planId}/snapshot`
  const response = await fetch(uri)
  const snapshots = normalizeKeys(response.data || [])
  return Array.isArray(snapshots) ? snapshots : []
}

export const fetchSnapshotWindow = async ({ productId, planId }) => {
  const uri = `${Endpoints.LiveReport}/${productId}/${planId}/snapshot/window`
  const response = await fetch(uri)
  return normalizeKeys(response.data || {})
}
